import React from 'react';
import { Link } from 'react-router-dom';
import useFleetStore from '../../stores/useFleetStore';
import dayjs from 'dayjs';
import Panel from '../../components/ui/Panel';
import EmptyState from '../../components/ui/EmptyState';
import Tag from '../../components/ui/Tag';
import { CATEGORY } from '../../components/ui/categoryColors';

const FlagAgingModule = () => {
  const flags = useFleetStore((state) => state.flags);

  if (flags.length === 0) return null;

  const now = dayjs();
  const aged = flags
    .filter(f => f.active && f.dateflagged)
    .map(f => ({
      ...f,
      daysOpen: now.diff(dayjs(f.dateflagged), 'day'),
    }))
    .sort((a, b) => b.daysOpen - a.daysOpen);

  const buckets = {
    critical: aged.filter(f => f.daysOpen > 30),
    warning: aged.filter(f => f.daysOpen > 14 && f.daysOpen <= 30),
    watch: aged.filter(f => f.daysOpen > 7 && f.daysOpen <= 14),
  };

  const staleCount = buckets.critical.length + buckets.warning.length + buckets.watch.length;
  if (staleCount === 0) {
    return (
      <Panel label="Flag aging">
        <EmptyState variant="area">No stale flags (all under 7 days)</EmptyState>
      </Panel>
    );
  }

  const renderBucket = (label, items, hue) => {
    if (items.length === 0) return null;
    return (
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: CATEGORY[hue].mark.oklch }} />
          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: CATEGORY[hue].ink.oklch }}>{label}</span>
          <span className="text-[10px] text-on-surface-variant tabular-nums">{items.length}</span>
        </div>
        <div className="space-y-1 pl-4">
          {items.slice(0, 5).map(f => (
            <Link key={f.id} to={`/drone/${f.drone_serial}`}
              className="flex items-center justify-between gap-2 text-xs hover:bg-gray-700/20 rounded px-2 py-1 -mx-2 transition-colors">
              <span className="text-on-surface font-mono">{f.drone_serial?.slice(-6)}</span>
              <span className="text-on-surface-variant truncate flex-1 mx-2">{f.descript}</span>
              <span className="font-semibold tabular-nums" style={{ color: CATEGORY[hue].ink.oklch }}>{f.daysOpen}d</span>
            </Link>
          ))}
          {items.length > 5 && (
            <div className="text-[10px] text-on-surface-disabled px-2">+{items.length - 5} more</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Panel
      title="Flag Aging"
      titleStyle="section"
      accent="red"
      lit
      level={3}
      actions={<Tag size="sm" hex={CATEGORY.red.ink.hex}>{staleCount} stale</Tag>}
    >
      <div className="space-y-3">
        {renderBucket('30+ days', buckets.critical, 'red')}
        {renderBucket('14–30 days', buckets.warning, 'orange')}
        {renderBucket('7–14 days', buckets.watch, 'yellow')}
      </div>
    </Panel>
  );
};

export default FlagAgingModule;
